<template>
  <div>
    <!-- Bug report button that opens modal -->
    <button
        :class="buttonClass"
        @click="showModal = true"
        type="button"
    >
      {{ buttonText }}
    </button>

    <!-- Modal dialog -->
    <div v-if="showModal" class="modal-backdrop" @click.self="showModal = false">
      <div class="modal-content">
        <div class="modal-header">
          <h5>{{ __DICT__.bugReport.reportABug }}</h5>
          <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
          <button type="button" class="close-button" @click="showModal = false">&times;</button>
        </div>

        <div class="modal-body">
          <div v-if="submitted">
            <div class="alert alert-success">
              {{ __DICT__.bugReport.thankYou }}
            </div>
          </div>
          <form v-else @submit.prevent="submitReport">
            <div class="form-group mb-3">
              <label for="bugDescription" class="form-label">{{ __DICT__.bugReport.whatWentWrong }}</label>
              <textarea
                  id="bugDescription"
                  v-model="report.description"
                  class="form-control"
                  rows="3"
                  placeholder="Please describe what happened..."
                  required
              ></textarea>
            </div>

            <div class="form-group mb-3">
              <label for="bugSteps" class="form-label">{{ __DICT__.bugReport.stepsToReproduce }}</label>
              <textarea
                  id="bugSteps"
                  v-model="report.steps"
                  class="form-control"
                  rows="2"
                  placeholder="What were you doing when this happened?"
              ></textarea>
            </div>

            <div class="form-group mb-3">
              <div class="form-check">
                <input
                    type="checkbox"
                    id="includeDeviceInfo"
                    v-model="report.includeDeviceInfo"
                    class="form-check-input"
                />
                <label for="includeDeviceInfo" class="form-check-label">
                  {{ __DICT__.bugReport.includeDeviceInfo }}
                </label>
              </div>
              <small class="form-text text-muted">
                {{ __DICT__.bugReport.helpsDiagnose }}
              </small>
            </div>

            <div class="d-flex justify-content-end">
              <button type="button" class="btn btn-secondary me-2" @click="showModal = false">{{ __DICT__.cancel }}</button>
              <button type="submit" class="btn btn-primary" :disabled="isSubmitting">
                {{ isSubmitting ? __DICT__.bugReport.submitting : __DICT__.bugReport.submitReport }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive } from 'vue'
import * as Sentry from '@sentry/vue'
import {useWeekStore} from "@/stores/week.js";

const props = defineProps({
  contextData: {
    type: Object,
    default: () => ({})
  },
  buttonText: {
    type: String,
    default: 'Report a Bug'
  },
  buttonClass: {
    type: String,
    default: 'btn btn-sm btn-outline-danger'
  }
})

const weekStore = useWeekStore()

const showModal = ref(false)
const isSubmitting = ref(false)
const submitted = ref(false)

const report = reactive({
  description: '',
  steps: '',
  email: '',
  includeDeviceInfo: true
})

const getDeviceInfo = () => {
  if (!report.includeDeviceInfo) return {}

  return {
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    cookiesEnabled: navigator.cookieEnabled,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    windowWidth: window.innerWidth,
    windowHeight: window.innerHeight,
    devicePixelRatio: window.devicePixelRatio || 1
  }
}

const submitReport = async () => {
  try {
    isSubmitting.value = true

    // Get device information if the user allowed it
    const deviceInfo = getDeviceInfo()

    // Prepare feedback data
    const feedbackData = {
      // Include passed context data
      ...props.contextData,

      // User-provided information
      description: report.description,
      steps: report.steps || 'Not provided',
      email: weekStore.repObj?.repId || 'unknown',

      // Device information
      deviceInfo
    }

    // Send to Sentry as user feedback
    Sentry.captureMessage('User Bug Report', {
      level: 'error',
      tags: {
        source: 'bug_reporter',
        component: 'QrScanner'
      },
      extra: feedbackData
    })

    // If user provided email, also send as user feedback
    if (report.email) {
      Sentry.captureFeedback({
        email: report.email,
        message: report.description,
        name: 'App User',
      })
    }

    // Reset form and show success
    submitted.value = true

    // Close modal after a delay
    setTimeout(() => {
      showModal.value = false
      submitted.value = false
      report.description = ''
      report.steps = ''
      report.email = ''
    }, 3000)
  } catch (error) {
    console.error('Error submitting bug report:', error)

    // Capture the error itself
    Sentry.captureException(error, {
      tags: {
        source: 'bug_reporter',
        operation: 'submit_report'
      }
    })
  } finally {
    isSubmitting.value = false
  }
}
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1050;
}

.modal-content {
  background: var(--tblr-bg-surface);
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: var(--tblr-box-shadow-lg);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid var(--tblr-border-color);
}

.modal-body {
  padding: 1rem;
}

.close-button {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--tblr-secondary-color);
}

.close-button:hover {
  color: var(--tblr-body-color);
}
</style>